/**
 * Генератор QR-кода
 * ====================================
 */
import { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData, useLocation } from "react-router";
import { debounce } from "lodash-es";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";

import ArticleImage from "~/components/ArticleImage";
import FormGroup from "~/components/FormGroup";
import TextInput from "~/components/forms/TextInput";
import H2 from "~/components/H2";
import H3 from "~/components/H3";
import Label from "~/components/Label";
import QRCodeGenerator from "~/components/QRCodeGenerator";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import ToolHeader from "~/components/tool-page/ToolHeader";
import { celebrities } from "~/helpers/numbers/age.server";
import { makeTitle } from "~/helpers/pageTitle";
import { createArticleSchema, createFAQSchema } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";

const CATEGORY = "tools";
const TKEY = "calculators.tools.qr_generator";
const ROUTE_ENDING = "qr";


export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const result = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description},
    createArticleSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      ["https://static.countlike.pro/qr-code-generator.webp"]
    ),
  ];
  const faqSchema = createFAQSchema(data?.faq ?? []);
  if (faqSchema) result.push(faqSchema);
  return result;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"]
};


export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";


  const t = await getTranslator(request);

  // Calculate result if query is set

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  const faq = [
    { q: t(`${TKEY}.info_what_is_h`), a: t(`${TKEY}.info_what_is_p`) },
    { q: t(`${TKEY}.info_how_to_create_h`), a: t(`${TKEY}.info_how_to_create_p`) },
    { q: t(`${TKEY}.info_expiration_h`), a: t(`${TKEY}.info_expiration_p`) },
    { q: t(`${TKEY}.info_commercial_use_h`), a: t(`${TKEY}.info_commercial_use_p`) },
    { q: t(`${TKEY}.info_my_data_h`), a: t(`${TKEY}.info_my_data_p`) },
    { q: t(`${TKEY}.info_why_not_working_h`), a: t(`${TKEY}.info_why_not_working_p`) },
  ];

  return {
    result,
    celebrities: celebrities[locale] ?? [],
    faq,
    meta: {
      locale,
      title: t(`${TKEY}.title`),
      description: t(`${TKEY}.description`),
      breadcrumbs
    }
  };
};

export default function Index() {
  const {
    meta
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);
  invariant(locale);


  const [isMounted, setIsMounted] = useState(false);
  const [url, setUrl] = useState("https://countlike.pro");

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const debouncedSetUrl = debounce((value) => setUrl(value), 300);

  return (
    <>
      <ToolHeader
        name={t(`${TKEY}.h1`)}
        description={t(`${TKEY}.description_extended`)}
      />

      <ToolForm>
        <FormGroup className="w-full">
          <Label
            htmlFor="url"
            text="URL"
          />
          <TextInput
            type="text"
            name="url"
            value={url}
            ariaLabel={t(`${TKEY}.qr_data`)}
            onChange={debouncedSetUrl}
          />
        </FormGroup>

        {isMounted ? (
          <QRCodeGenerator
            data={url}
            tkey={TKEY}
          />
        ) : null}
      </ToolForm>

      <AdditionalInfo>
        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_what_is_h`)}</H2>
          <p>{t(`${TKEY}.info_what_is_p`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_how_to_create_h`)}</H2>
          <p>{t(`${TKEY}.info_how_to_create_p`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_how_to_scan_h`)}</H2>
          <p>{t(`${TKEY}.info_how_to_scan_p`)}</p>
          <ul className="list-disc list-inside">
            <li>{t(`${TKEY}.info_how_to_scan_li_1`)}</li>
            <li>{t(`${TKEY}.info_how_to_scan_li_2`)}</li>
            <li>{t(`${TKEY}.info_how_to_scan_li_3`)}</li>
            <li>{t(`${TKEY}.info_how_to_scan_li_4`)}</li>
          </ul>
          <p>{t(`${TKEY}.info_how_to_scan_p_2`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_expiration_h`)}</H2>
          <p>{t(`${TKEY}.info_expiration_p`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_static_dynamic_h`)}</H2>
          <p>{t(`${TKEY}.info_static_dynamic_p_1`)}</p>
          <p>{t(`${TKEY}.info_static_dynamic_p_2`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_printing_h`)}</H2>
          <p>{t(`${TKEY}.info_printing_p`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_commercial_use_h`)}</H2>
          <p>{t(`${TKEY}.info_commercial_use_p`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_my_data_h`)}</H2>
          <p>{t(`${TKEY}.info_my_data_p`)}</p>
        </AdditionalInfoSection>

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_why_not_working_h`)}</H2>
          <p>{t(`${TKEY}.info_why_not_working_p`)}</p>
        </AdditionalInfoSection>

        <ArticleImage
          src="https://static.countlike.pro/a-person-scanning-generated-qr-code.webp"
          alt="A tourist scanning a generated QR code on a landmark"
          caption=""
          width={1200}
          height={686}
        />

        <AdditionalInfoSection>
          <H2>{t(`${TKEY}.info_use_cases_h`)}</H2>
          <H3>{t(`${TKEY}.info_use_cases_products_h`)}</H3>
          <p>{t(`${TKEY}.info_use_cases_products_p`)}</p>
          <H3>{t(`${TKEY}.info_use_cases_vcards_h`)}</H3>
          <p>{t(`${TKEY}.info_use_cases_vcards_p`)}</p>
          <H3>{t(`${TKEY}.info_use_cases_marketing_h`)}</H3>
          <p>{t(`${TKEY}.info_use_cases_marketing_p`)}</p>
          <H3>{t(`${TKEY}.info_use_cases_tourism_h`)}</H3>
          <p>{t(`${TKEY}.info_use_cases_tourism_p`)}</p>
        </AdditionalInfoSection>
      </AdditionalInfo>
    </>
  );
}


